// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

interface XFrequentlyBoughtTogetherProductsStateType {
    [key: string]: {
        checkedProducts: { [id: string]: ProductInfo };
        totalCost: number;
    };
}

export const initialFrequentlyBoughtTogetherProductsState: XFrequentlyBoughtTogetherProductsStateType = {};

const reducer = {
    "xFrequentlyBoughtTogetherProducts/reset": (
        draft: Draft<XFrequentlyBoughtTogetherProductsStateType>,
        action: { key: string },
    ) => {
        draft[action.key].totalCost = 0;
        draft[action.key].checkedProducts = {};
    },
    "xFrequentlyBoughtTogetherProducts/updateSelection": (
        draft: Draft<XFrequentlyBoughtTogetherProductsStateType>,
        action: { productInfo: ProductInfo; key: string },
    ) => {
        if (draft[action.key]) {
            if (draft[action.key].checkedProducts && draft[action.key].checkedProducts[action.productInfo.productId]) {
                delete draft[action.key].checkedProducts[action.productInfo.productId];
            } else if (draft[action.key].checkedProducts) {
                draft[action.key].checkedProducts[action.productInfo.productId] = action.productInfo;
            } else {
                draft[action.key].checkedProducts = {};
                draft[action.key].checkedProducts[action.productInfo.productId] = action.productInfo;
            }
        } else {
            draft[action.key] = {
                checkedProducts: {
                    [action.productInfo.productId]: action.productInfo,
                },
                totalCost: 0,
            };
        }

        let totalCost = 0;

        Object.values(draft[action.key].checkedProducts).forEach(product => {
            totalCost += product.pricing?.unitNetPrice || 0;
        });
        draft[action.key].totalCost = totalCost;
    },
    "xFrequentlyBoughtTogetherProducts/selectAll": (
        draft: Draft<XFrequentlyBoughtTogetherProductsStateType>,
        action: { products: ProductModel[]; key: string; carouselId: string; state: ApplicationState },
    ) => {
        if (!draft[action.key]) {
            draft[action.key] = { checkedProducts: {}, totalCost: 0 };
        }

        let totalCost = 0;

        action.products.forEach(product => {
            const productInfo = getProductInfoFromList(action.state, action.carouselId, product.id);
            if (productInfo) {
                draft[action.key].checkedProducts[product.id] = productInfo;
                totalCost += productInfo?.pricing?.unitNetPrice || 0;
            }
        });

        draft[action.key].totalCost = totalCost;
    },
};

const xFrequentlyBoughtTogetherProducts = createTypedReducerWithImmer(
    initialFrequentlyBoughtTogetherProductsState,
    reducer,
);
(reducers as any).xFrequentlyBoughtTogetherProducts = xFrequentlyBoughtTogetherProducts;

export type xFrequentlyBoughtTogetherProductsState = {
    xFrequentlyBoughtTogetherProducts: XFrequentlyBoughtTogetherProductsStateType;
} & ApplicationState;

type xFrequentlyBoughtTogetherProductsReducers = {
    [key: string]: typeof xFrequentlyBoughtTogetherProducts;
};

export type xFrequentlyBoughtTogetherProductsActions =
    | Parameters<xFrequentlyBoughtTogetherProductsReducers[keyof xFrequentlyBoughtTogetherProductsReducers]>[1]
    | AnyAction;
